import type { V2AiObservation } from './aiObservation';

// Cross-batch duplicates (2026-10-19, the Owner's 8 points, #3). An area with more than AI_V2_LIMITS.maxPhotos
// photos is analyzed in several batches (the pool analysis, src/state/poolAiStore.ts) and every batch ADDS
// its observations to the area. The 160-photo runs showed the same driveway cracks, the same siding gap, the
// same deck paint three times — one per batch — because nothing compared a batch's observations with the
// ones already on the row. This module does, on the device, without the model:
//   - two observations are the SAME CONDITION when they sit on the same row (or are both unclassified) with
//     the same verdict and the same kind, and their headlines say the same thing — identical once normalized,
//     or sharing most of their character bigrams (Chinese) / words (English);
//   - the earlier observation is kept and gains the newer one's photos; the newer one is dropped. Its text is
//     not merged: two sentences about one crack read worse than one, and the inspector sees the photos anyway.
// Nothing here touches an observation the inspector already resolved, and a different verdict on the same
// row is never merged (a crack and a "nothing seen" are two observations, as core/analyze.ts keeps them).
// The row title is NOT a reason to merge: two different conditions on one row carry the same title.
const SIMILARITY = 0.5;

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\s\p{P}\p{S}]+/gu, '')
    .trim();
}

function tokens(value: string): Set<string> {
  const normalized = normalize(value);
  if (normalized.length === 0) return new Set();
  // Latin text: words; CJK text: character bigrams (a single character is too common to count).
  if (/^[a-z0-9]+$/.test(normalized)) return new Set(value.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 1));
  const out = new Set<string>();
  for (let i = 0; i < normalized.length - 1; i += 1) out.add(normalized.slice(i, i + 2));
  if (normalized.length === 1) out.add(normalized);
  return out;
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const t of a) if (b.has(t)) shared += 1;
  return shared / (a.size + b.size - shared);
}

export function sameCondition(a: Pick<V2AiObservation, 'itemId' | 'type' | 'verdict' | 'headline'>, b: Pick<V2AiObservation, 'itemId' | 'type' | 'verdict' | 'headline'>): boolean {
  if (a.itemId !== b.itemId || a.type !== b.type) return false;
  if ((a.verdict ?? null) !== (b.verdict ?? null)) return false;
  const ha = normalize(a.headline);
  const hb = normalize(b.headline);
  if (ha.length > 0 && ha === hb) return true;
  return jaccard(tokens(a.headline), tokens(b.headline)) >= SIMILARITY;
}

export interface MergeOutcome {
  // The incoming observations that are new conditions, in their original order.
  fresh: V2AiObservation[];
  // Earlier observations that gained photos from a dropped duplicate (to persist again).
  updated: V2AiObservation[];
  // How many incoming observations were folded into an earlier one.
  folded: number;
}

// Folds the incoming batch into the area's existing, still-open observations (resolved ones are left alone
// and never absorb anything). Duplicates inside the incoming batch itself are folded too.
export function mergeAppendedObservations(existing: readonly V2AiObservation[], incoming: readonly V2AiObservation[], now: number): MergeOutcome {
  const pool: V2AiObservation[] = existing.filter((o) => o.resolution === null).map((o) => ({ ...o }));
  const touched = new Set<string>();
  const fresh: V2AiObservation[] = [];
  let folded = 0;
  for (const candidate of incoming) {
    const twin = pool.find((o) => sameCondition(o, candidate));
    if (!twin) {
      pool.push(candidate);
      fresh.push(candidate);
      continue;
    }
    folded += 1;
    const photoIds = [...new Set([...twin.photoIds, ...candidate.photoIds])];
    if (photoIds.length !== twin.photoIds.length) {
      twin.photoIds = photoIds;
      twin.updatedAt = Math.max(twin.updatedAt, now);
      if (!fresh.includes(twin)) touched.add(twin.id);
    }
  }
  const freshIds = new Set(fresh.map((o) => o.id));
  const updated = pool.filter((o) => touched.has(o.id) && !freshIds.has(o.id));
  // A fresh observation that absorbed a later duplicate of the same batch carries the union of photos.
  const freshOut = fresh.map((o) => pool.find((p) => p.id === o.id) ?? o);
  return { fresh: freshOut, updated, folded };
}
